import { useState } from "react";
import { Link } from "react-router-dom";
import { Separator } from "@/components/ui/separator";
import { ChevronDown } from "lucide-react";

const footerSections = [
  {
    title: "Solutions",
    links: [
      { name: "Distributors", path: "/solutions/distributors" },
      { name: "Suppliers", path: "/solutions/suppliers" },
      { name: "Decorators", path: "/solutions/decorators" },
      { name: "Print Shops", path: "/solutions/print-shops" },
      { name: "Sign Companies", path: "/solutions/sign-companies" },
      { name: "QuickBooks Users", path: "/solutions/quickbooks" },
    ],
  },
  {
    title: "Products",
    links: [
      { name: "Advance", path: "/advance" },
      { name: "Aether", path: "/aether" },
      { name: "Integrations", path: "/integrations" },
      { name: "API Documentation", path: "/developers" },
    ],
  },
  {
    title: "Company",
    links: [
      { name: "Why Antera", path: "/why" },
      { name: "Exchange", path: "/exchange" },
      { name: "Insights", path: "/blog" },
      { name: "Careers", path: "/careers" },
      { name: "Contact", path: "/contact" },
    ],
  },
  {
    title: "Legal & Trust",
    links: [
      { name: "Privacy Policy", path: "/privacy-policy" },
      { name: "Terms of Service", path: "/terms-of-service" },
      { name: "Security / SOC 2", path: "/security" },
      { name: "Accessibility", path: "/accessibility" },
    ],
  },
];

export default function Footer() {
  return (
    <footer data-testid="site-footer" className="bg-slate-900 text-white relative">
      {/* Gradient top edge */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-slate-700 to-transparent" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14 lg:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-6 md:gap-10 lg:gap-12">
          {/* Brand */}
          <div className="lg:col-span-2">
            <img
              src="/api/media/d26db921-09fb-4125-a499-14d92437fc19"
              alt="Antera Software"
              className="h-10 w-auto mb-4"
              data-testid="footer-logo"
            />
            <p className="text-slate-400 text-sm leading-relaxed max-w-sm">
              Cloud-based ERP, CRM, and eCommerce solutions for promotional products 
              and print businesses. Simplify operations, reduce errors, and 
              fuel growth.
            </p>
            {/* Social Icons */}
            <div className="flex items-center gap-3 mt-5">
              <a href="https://www.youtube.com/@anterausa" target="_blank" rel="noopener noreferrer" data-testid="footer-social-youtube" className="w-9 h-9 rounded-full bg-slate-800 hover:bg-sky-600 flex items-center justify-center transition-colors" aria-label="YouTube">
                <svg className="w-4 h-4 fill-current text-slate-400 hover:text-white" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
              </a>
              <a href="https://www.instagram.com/anterausa/" target="_blank" rel="noopener noreferrer" data-testid="footer-social-instagram" className="w-9 h-9 rounded-full bg-slate-800 hover:bg-sky-600 flex items-center justify-center transition-colors" aria-label="Instagram">
                <svg className="w-4 h-4 fill-current text-slate-400 hover:text-white" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M12 0C8.74 0 8.333.015 7.053.072 5.775.132 4.905.333 4.14.63c-.789.306-1.459.717-2.126 1.384S.935 3.35.63 4.14C.333 4.905.131 5.775.072 7.053.012 8.333 0 8.74 0 12s.015 3.667.072 4.947c.06 1.277.261 2.148.558 2.913.306.788.717 1.459 1.384 2.126.667.666 1.336 1.079 2.126 1.384.766.296 1.636.499 2.913.558C8.333 23.988 8.74 24 12 24s3.667-.015 4.947-.072c1.277-.06 2.148-.262 2.913-.558.788-.306 1.459-.718 2.126-1.384.666-.667 1.079-1.335 1.384-2.126.296-.765.499-1.636.558-2.913.06-1.28.072-1.687.072-4.947s-.015-3.667-.072-4.947c-.06-1.277-.262-2.149-.558-2.913-.306-.789-.718-1.459-1.384-2.126C21.319 1.347 20.651.935 19.86.63c-.765-.297-1.636-.499-2.913-.558C15.667.012 15.26 0 12 0zm0 2.16c3.203 0 3.585.016 4.85.071 1.17.055 1.805.249 2.227.415.562.217.96.477 1.382.896.419.42.679.819.896 1.381.164.422.36 1.057.413 2.227.057 1.266.07 1.646.07 4.85s-.015 3.585-.074 4.85c-.061 1.17-.256 1.805-.421 2.227-.224.562-.479.96-.899 1.382-.419.419-.824.679-1.38.896-.42.164-1.065.36-2.235.413-1.274.057-1.649.07-4.859.07-3.211 0-3.586-.015-4.859-.074-1.171-.061-1.816-.256-2.236-.421-.569-.224-.96-.479-1.379-.899-.421-.419-.69-.824-.9-1.38-.165-.42-.359-1.065-.42-2.235-.045-1.26-.061-1.649-.061-4.844 0-3.196.016-3.586.061-4.861.061-1.17.255-1.814.42-2.234.21-.57.479-.96.9-1.381.419-.419.81-.689 1.379-.898.42-.166 1.051-.361 2.221-.421 1.275-.045 1.65-.06 4.859-.06l.045.03zm0 3.678a6.162 6.162 0 100 12.324 6.162 6.162 0 100-12.324zM12 16c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4zm7.846-10.405a1.441 1.441 0 11-2.88 0 1.441 1.441 0 012.88 0z"/></svg>
              </a>
              <a href="https://www.facebook.com/Anterasoftware" target="_blank" rel="noopener noreferrer" data-testid="footer-social-facebook" className="w-9 h-9 rounded-full bg-slate-800 hover:bg-sky-600 flex items-center justify-center transition-colors" aria-label="Facebook">
                <svg className="w-4 h-4 fill-current text-slate-400 hover:text-white" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
              </a>
              <a href="https://www.linkedin.com/company/antera-software" target="_blank" rel="noopener noreferrer" data-testid="footer-social-linkedin" className="w-9 h-9 rounded-full bg-slate-800 hover:bg-sky-600 flex items-center justify-center transition-colors" aria-label="LinkedIn">
                <svg className="w-4 h-4 fill-current text-slate-400 hover:text-white" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
              </a>
              <a href="https://x.com/AnteraUSA" target="_blank" rel="noopener noreferrer" data-testid="footer-social-x" className="w-9 h-9 rounded-full bg-slate-800 hover:bg-sky-600 flex items-center justify-center transition-colors" aria-label="X (Twitter)">
                <svg className="w-4 h-4 fill-current text-slate-400 hover:text-white" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.154h7.594l5.243 6.932ZM17.61 20.644h2.039L6.486 3.24H4.298Z"/></svg>
              </a>
            </div>
          </div>

          {/* Link Sections - collapsible on mobile, open on md+ */}
          {footerSections.map((section) => (
            <FooterSection key={section.title} section={section} />
          ))}
        </div>

        <Separator className="my-8 sm:my-10 bg-slate-800" />

        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-4 text-center sm:text-left">
          <p className="text-xs sm:text-sm text-slate-500">
            &copy; {new Date().getFullYear()} Antera Software, USA. All Rights Reserved.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-6">
            <span className="text-xs sm:text-sm text-slate-500">
              Designed By{" "}
              <a
                href="https://bp.agency"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-brand-partners-link"
                className="font-bold text-slate-400 hover:text-white transition-colors"
              >
                Brand Partners
              </a>
            </span>
          </div>
        </div>
      </div>
      {/* Bottom safe-area for mobile floating widgets (Intercom + a11y) */}
      <div className="h-20 md:h-0 bg-slate-900" aria-hidden="true" />
    </footer>
  );
}

function FooterSection({ section }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-t md:border-t-0 border-slate-800 first:border-t-0 md:first:border-t-0">
      {/* Mobile: collapsible toggle */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="md:hidden w-full flex items-center justify-between py-3.5 text-left"
        data-testid={`footer-toggle-${section.title.toLowerCase()}`}
      >
        <h4 className="text-sm font-semibold text-white tracking-wide uppercase">{section.title}</h4>
        <ChevronDown
          className={`h-4 w-4 text-slate-400 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
        />
      </button>

      {/* Desktop: always-visible header */}
      <h4 className="hidden md:block text-sm font-semibold text-white tracking-wide uppercase mb-4">
        {section.title}
      </h4>

      <ul
        className={`md:block space-y-2.5 overflow-hidden transition-all duration-300 ${
          open ? "max-h-96 opacity-100 pb-4" : "max-h-0 opacity-0 md:max-h-none md:opacity-100"
        }`}
      >
        {section.links.map((link) => (
          <li key={link.name}>
            {link.external ? (
              <a
                href={link.path}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`footer-${link.name.toLowerCase().replace(/\s/g, "-")}`}
                className="text-sm text-slate-400 hover:text-white transition-colors"
              >
                {link.name}
              </a>
            ) : (
              <Link
                to={link.path}
                data-testid={`footer-${link.name.toLowerCase().replace(/\s/g, "-")}`}
                className="text-sm text-slate-400 hover:text-white transition-colors"
              >
                {link.name}
              </Link>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
